import {
  Box,
  Button,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Text,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import TableLoader from "components/common/TableLoader/TableLoader";
import { FieldArray } from "formik";
import { FaPlus } from "react-icons/fa";
import UserApprovalForeign from "./UserApprovalForeign";
import { foreignDesignations, isActiveUser, levels, roles } from "./utils/constant";
import CustomInput from "components/common/customInput/CustomInput";
import { useEffect } from "react";

const ApprovalsFormForeign = ({
  activePage,
  values,
  setFieldValue,
  isDisabled,
  setDeletedApproavals,
  type,
  loadOptions,
  data,
  showError,
  generateLevels,
  isLoading,
  debouncedLoadOptions,
}: any) => {

  const selectedRegions = Array.isArray(values.regions) ? values.regions : (values.regions ? [values.regions] : []);

  useEffect(() => {
    // Only use regional tab logic for 'create' mode or if multi-region management is active
    if (type === 'edit') return;

    // Determine the default template (from values.approvals or any existing region)
    const defaultApprovals = values.approvals || [];
    
    if (selectedRegions.length > 0 && !values.regionApprovals) {
      const initialRegionApprovals: any = {};
      selectedRegions.forEach((region: any) => {
        const regionValue = region.value || region;
        initialRegionApprovals[regionValue] = {
          approvals: [...defaultApprovals]
        };
      });
      setFieldValue("regionApprovals", initialRegionApprovals);
    } else if (selectedRegions.length > 0 && values.regionApprovals) {
      const updatedRegionApprovals = { ...values.regionApprovals };
      let changed = false;
      
      selectedRegions.forEach((region: any) => {
        const regionValue = region.value || region;
        if (!updatedRegionApprovals[regionValue]) {
          updatedRegionApprovals[regionValue] = {
            approvals: [...defaultApprovals]
          };
          changed = true;
        }
      });
      if (changed) {
        setFieldValue("regionApprovals", updatedRegionApprovals);
      }
    }
  }, [values.regions, setFieldValue, type]);

  if (selectedRegions.length === 0 && type !== 'edit') {
    return (
      <Box display={activePage === 1 ? undefined : "none"} textAlign="center" py={10}>
        <Text fontSize="lg" color="gray.500">Please select at least one region in the first step.</Text>
      </Box>
    );
  }

  // Use tabbed UI only for 'create' mode with regions
  const showRegionalTabs = type !== 'edit' && selectedRegions.length > 0 && values.regionApprovals;

  if (!showRegionalTabs) {
    return (
      <Box display={activePage === 1 ? undefined : "none"}>
        <Flex justifyContent="space-between" mb={4}>
          <Flex alignItems="center" gap={3}>
            <Text fontWeight="500" whiteSpace="nowrap">
              Differentiate First Level Users:
            </Text>
            <CustomInput
              disabled={isDisabled}
              type="switch"
              value={values.differentiateFirstLevel}
              name="differentiateFirstLevel"
              onChange={(e: any) =>
                setFieldValue(`differentiateFirstLevel`, e.target.checked)
              }
            />
          </Flex>
        </Flex>

        <Box mt={2} position="relative" overflow="auto" className="customScrollBar">
          <FieldArray name="approvals">
            {({ push, remove }) => (
              <Box>
                <TableContainer overflow="auto" className="customScrollBar">
                  <Table variant={"striped"}>
                    <Thead>
                      <Tr>
                        <Th w="33%" whiteSpace="nowrap">User Name</Th>
                        <Th w="33%" whiteSpace="nowrap">Designation</Th>
                        <Th w="33%" whiteSpace="nowrap">Keep Active</Th>
                        <Th display="none" whiteSpace="nowrap">Role</Th>
                        <Th display="none" whiteSpace="nowrap">Level</Th>
                        <Th></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Array.isArray(values.approvals) &&
                        values.approvals.map((item: any, index: number) => (
                          <UserApprovalForeign
                            key={item?.user || index}
                            index={index}
                            item={item}
                            values={values}
                            setFieldValue={setFieldValue}
                            remove={remove}
                            isDisabled={isDisabled}
                            loadOptions={loadOptions}
                            generateLevels={generateLevels}
                            isLoading={isLoading}
                            debouncedLoadOptions={debouncedLoadOptions}
                          />
                        ))}
                    </Tbody>
                  </Table>
                </TableContainer>
                <Flex justifyContent="flex-end" mt={4}>
                  <Button
                    leftIcon={<FaPlus />}
                    colorScheme="teal"
                    variant="outline"
                    size="sm"
                    onClick={() => push({
                      username: "",
                      role: roles[1],
                      level: generateLevels(values.levels || 7)[1],
                      isActive: isActiveUser[0],
                      canUpload: isActiveUser[0],
                      designation: foreignDesignations[Array.isArray(values.approvals) ? values.approvals.length : 0] || foreignDesignations[0],
                      userPermission: [],
                      selectMode: "select-async",
                      addNew: 1
                    })}
                  >
                    Add User
                  </Button>
                </Flex>
              </Box>
            )}
          </FieldArray>
        </Box>
      </Box>
    );
  }

  return (
    <Box display={activePage === 1 ? undefined : "none"}>
      <Flex justifyContent="space-between" mb={4}>
        <Flex alignItems="center" gap={3}>
          <Text fontWeight="500" whiteSpace="nowrap">
            Differentiate First Level Users:
          </Text>
          <CustomInput
            disabled={isDisabled}
            type="switch"
            value={values.differentiateFirstLevel}
            name="differentiateFirstLevel"
            onChange={(e: any) =>
              setFieldValue(`differentiateFirstLevel`, e.target.checked)
            }
          />
        </Flex>
      </Flex>

      <Tabs variant="enclosed" colorScheme="teal" mt={4}>
        <TabList
          overflowX="auto"
          overflowY="hidden"
          className="customScrollBar"
          gap={2}
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          {selectedRegions.map((region: any, idx: number) => (
            <Tab
              key={idx}
              whiteSpace="nowrap"
              borderRadius="md"
              borderBottom="none"
              _selected={{ color: "white", bg: "teal.400" }}
            >
              {region.label || region}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {selectedRegions.map((region: any, idx: number) => {
            const regionValue = region.value || region;
            const regionPath = `regionApprovals.${regionValue}.approvals`;
            const approvals = values.regionApprovals?.[regionValue]?.approvals || [];

            return (
              <TabPanel key={idx} px={0}>
                <Box mt={2} position="relative" overflow="auto" className="customScrollBar">
                  <TableContainer overflow="auto" className="customScrollBar">
                    <Table variant={"striped"}>
                      <Thead>
                        <Tr>
                          <Th w="33%" whiteSpace="nowrap">User Name</Th>
                          <Th w="33%" whiteSpace="nowrap">Designation</Th>
                          <Th w="33%" whiteSpace="nowrap">Keep Active</Th>
                          <Th display="none" whiteSpace="nowrap">Role</Th>
                          <Th display="none" whiteSpace="nowrap">Level</Th>
                          <Th></Th>
                        </Tr>
                      </Thead>
                      <FieldArray name={regionPath}>
                        {({ remove, push }) => (
                          <>
                            <TableLoader
                              loader={false}
                              show={approvals.length}
                              noDataText="Create a New Initial User"
                            >
                              <Tbody>
                                {approvals.map((item: any, index: number) => (
                                  <UserApprovalForeign
                                    key={item?.user || index}
                                    index={index}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    loadOptions={loadOptions}
                                    remove={remove}
                                    type={type}
                                    isDisabled={isDisabled}
                                    setDeletedApproavals={setDeletedApproavals}
                                    data={data}
                                    isLoading={isLoading}
                                    generateLevels={generateLevels}
                                    debouncedLoadOptions={debouncedLoadOptions}
                                    regionPath={regionPath}
                                    regionValue={regionValue}
                                  />
                                ))}
                              </Tbody>
                            </TableLoader>
                            <tfoot>
                              <Tr>
                                <Td colSpan={4}>
                                  <Flex justify="center" mt={4}>
                                    <Button
                                      leftIcon={<FaPlus />}
                                      size="sm"
                                      colorScheme="teal"
                                      onClick={() =>
                                          push({
                                            username: "",
                                            role: roles[1],
                                            level: generateLevels(values.levels || 7)[1],
                                            isActive: isActiveUser[0],
                                            designation: foreignDesignations[approvals.length] || foreignDesignations[0],
                                            addNew: 1,
                                            canUpload: isActiveUser[1],
                                            selectMode: "select-async",
                                          })
                                      }
                                      isDisabled={isDisabled}
                                    >
                                      Add User
                                    </Button>
                                  </Flex>
                                </Td>
                              </Tr>
                            </tfoot>
                          </>
                        )}
                      </FieldArray>
                    </Table>
                  </TableContainer>
                </Box>
              </TabPanel>
            );
          })}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default ApprovalsFormForeign;
